import { translateOutsideReact } from '@/i18n/client-translator';

import { toHumanReadableError } from './error';
import { matchSpentWalletInput } from './matchers';

export const TRANSACTION_ERRORS = {
  TIMEOUT: 'Time between submission and approval is too large, please reduce the time between submission and signining',
  NO_ACCOUNTS: 'No accounts assigned to this peer or all are locked',
  VAULT_DEPRECATED: 'Vault is deprecated: it was created with outdated guardian key. Please create a new vault.',
};

export const DEPRECATED_VAULT_SEARCH_TEXT = 'Vault is deprecated';

const formatErrorMessage = (message: string): string => {
  if (!message) return message;

  // Split the message into words
  return message
    .split(' ')
    .map((word) => {
      // Keep "to" lowercase, capitalize other words
      return word.toLowerCase() === 'to' ? 'to' : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const isMetaMaskPendingRequest = (message: string): boolean => {
  return /snap_dialog:confirmation/i.test(message) && /already pending/i.test(message);
};

const getUtxoConflictDisplay = (message: string): { title: string; detail: string } | null => {
  const title = translateOutsideReact('common.errors.transactionConflict');
  const detail = translateOutsideReact('common.errors.transactionConflictRetry');

  // Both the notification and confirmation panel parse action errors. Keep the
  // friendly copy intact on the second pass, without exposing request/outpoint IDs.
  if (!/\butxo[\s_-]*conflict\b/i.test(message) && message !== `${title} ${detail}`) return null;

  return { title, detail };
};

const getLiquidationPriceMismatch = (message: string): { expected: string; submitted: string } | null => {
  const match = /liquidation price mismatch between expected\s+([\d,.]+)\s+and specified in the stone\s+([\d,.]+)/i.exec(message);

  if (!match) return null;

  return {
    expected: match[1],
    submitted: match[2],
  };
};

const formatPrice = (value: string): string => {
  const amount = Number(value.replaceAll(',', ''));
  return Number.isFinite(amount) ? `$${amount.toLocaleString('en-US')}` : value;
};

const getGuardianContentionDisplay = (message: string): { title: string; detail: string } | null => {
  const title = translateOutsideReact('common.errors.guardianBusy');

  // Action errors are parsed for the toast and then again in the confirmation panel.
  if (message.startsWith(`${title} `)) {
    return { title, detail: message.slice(title.length + 1) };
  }

  // NoAccount also covers empty reserves; only temporary contention means "busy".
  if (!/\b(?:funded|asset) accounts are temporarily contended\b/i.test(message)) {
    return null;
  }

  const retryMatch = /\bretry after (\d+) seconds?\b/i.exec(message);
  const seconds = retryMatch ? Number(retryMatch[1]) : undefined;
  let detail = translateOutsideReact('common.errors.guardianRetrySoon');
  if (seconds !== undefined && Number.isSafeInteger(seconds)) {
    detail =
      seconds >= 60 && seconds % 60 === 0
        ? translateOutsideReact('common.errors.guardianRetryAfterMinutes', { minutes: seconds / 60 })
        : translateOutsideReact('common.errors.guardianRetryAfterSeconds', { seconds });
  }

  return { title, detail };
};

export const getErrorDisplay = (
  rawError: string,
  parsedError: string | null,
  localizedError: string | null,
): { title: string; detail?: string } | null => {
  if (!parsedError) return null;

  const guardianContention = getGuardianContentionDisplay(rawError) ?? getGuardianContentionDisplay(parsedError);
  if (guardianContention) return guardianContention;

  const utxoConflict = getUtxoConflictDisplay(rawError) ?? getUtxoConflictDisplay(parsedError);
  if (utxoConflict) return utxoConflict;

  const liquidationPriceMismatch = getLiquidationPriceMismatch(rawError) ?? getLiquidationPriceMismatch(parsedError);

  if (liquidationPriceMismatch) {
    return {
      title: 'Vault price changed before validation',
      detail: `The validator expected ${formatPrice(liquidationPriceMismatch.expected)}, but this request used ${formatPrice(
        liquidationPriceMismatch.submitted,
      )}. Refresh vault data and submit again.`,
    };
  }

  if (isMetaMaskPendingRequest(rawError)) {
    return {
      title: 'MetaMask confirmation already open',
      detail: 'Finish or reject the pending Ducat request in MetaMask, then try again.',
    };
  }

  if (parsedError === TRANSACTION_ERRORS.TIMEOUT) {
    return {
      title: 'Signature timed out',
      detail: localizedError ?? 'Create a fresh request and approve it sooner.',
    };
  }

  if (parsedError === TRANSACTION_ERRORS.NO_ACCOUNTS) {
    return {
      title: 'No guardian account available',
      detail: localizedError ?? "The system can't process this request right now. Please try again in 30 seconds.",
    };
  }

  return {
    title: 'Transaction could not continue',
    detail: localizedError ?? parsedError,
  };
};

// This utility defines a display error message that we show user in Snackbar and Drawer:
export function parseVaultError(errorMessage: string): string | null {
  const spentInputMessage = translateOutsideReact('common.errors.spentWalletInput');
  if (matchSpentWalletInput(errorMessage) || errorMessage === spentInputMessage) return spentInputMessage;

  if (
    ['common.disabledReason.nothingToRepay', 'common.disabledReason.walletDataLoading'].some(
      (key) => errorMessage === translateOutsideReact(key),
    )
  )
    return errorMessage;

  const guardianContention = getGuardianContentionDisplay(errorMessage);
  if (guardianContention) {
    return `${guardianContention.title} ${guardianContention.detail}`;
  }

  const utxoConflict = getUtxoConflictDisplay(errorMessage);
  if (utxoConflict) {
    return `${utxoConflict.title} ${utxoConflict.detail}`;
  }

  const humanReadable = toHumanReadableError(errorMessage);

  if (humanReadable) {
    return humanReadable;
  }

  if (typeof errorMessage === 'string') {
    if (/repay_ratio_worsened|cannot preserve the collateral ratio/i.test(errorMessage)) {
      return 'This partial repayment would lower the collateral ratio. Refresh the quote or repay the full balance.';
    }
    if (/above_maximum_ratio/i.test(errorMessage)) {
      return 'The remaining collateral exceeds the supported maximum ratio. Refresh the quote and review the collateral amount.';
    }

    if (isMetaMaskPendingRequest(errorMessage)) {
      return 'MetaMask confirmation already open';
    }

    const liquidationPriceMismatch = getLiquidationPriceMismatch(errorMessage);
    if (liquidationPriceMismatch) {
      return `Vault price changed before validation. Expected ${formatPrice(liquidationPriceMismatch.expected)}, request used ${formatPrice(
        liquidationPriceMismatch.submitted,
      )}.`;
    }

    if (errorMessage.includes(TRANSACTION_ERRORS.NO_ACCOUNTS)) {
      return TRANSACTION_ERRORS.NO_ACCOUNTS;
    }

    if (errorMessage.includes('Validation of DepositVault failed Error: Custom("Timestamp (') && errorMessage.includes(') not valid.")')) {
      return TRANSACTION_ERRORS.TIMEOUT;
    }

    if (errorMessage.includes(DEPRECATED_VAULT_SEARCH_TEXT)) {
      return TRANSACTION_ERRORS.VAULT_DEPRECATED;
    }

    if (errorMessage.includes('Account UTXO mismatch')) {
      return TRANSACTION_ERRORS.TIMEOUT;
    }

    if (errorMessage.includes('user timeout')) {
      return TRANSACTION_ERRORS.TIMEOUT;
    }

    if (errorMessage.includes('Custom(')) {
      const complexErrorMatch = /Custom\("(Failed to broadcast.*?)"\)/.exec(errorMessage);

      if (complexErrorMatch?.[1]) {
        return formatErrorMessage(complexErrorMatch[1].replace(/\\\\/g, '').replace(/\\"/g, '"').replace(/\\/g, ''));
      }

      const simpleCustomMatch = /Custom\("(.+?)"\)/.exec(errorMessage);

      if (simpleCustomMatch?.[1]) {
        return formatErrorMessage(simpleCustomMatch[1].replace(/\\"/g, '"').replace(/\\/g, ''));
      }
    }

    return formatErrorMessage(errorMessage);
  }

  return null;
}

/**
 * Known transaction errors are matched by their raw (English) sentinel value, so the
 * displayed copy is resolved from the catalogue instead of the sentinel itself.
 */
export const getTransactionErrorKey = (parsedError: string): string | undefined => {
  switch (parsedError) {
    case TRANSACTION_ERRORS.TIMEOUT:
      return 'timeout';
    case TRANSACTION_ERRORS.NO_ACCOUNTS:
      return 'noAccounts';
    case TRANSACTION_ERRORS.VAULT_DEPRECATED:
      return 'vaultDeprecated';
    default:
      return undefined;
  }
};
